import React from 'react';
import { useUser } from '@clerk/clerk-react';

const DataDeletionRequest: React.FC = () => {
  const { user } = useUser();

  const handleEmailRequest = () => {
    const subject = encodeURIComponent('CCPA Data Deletion Request');
    const body = encodeURIComponent(
      `I am requesting deletion of my personal data under the California Consumer Privacy Act (CCPA).\n\n` +
      `Account Email: ${user?.primaryEmailAddress?.emailAddress}\n` +
      `Request Date: ${new Date().toLocaleDateString()}\n\n` +
      `Please confirm receipt of this request and process the deletion within 45 days as required by law.\n\n` +
      `Thank you.`
    );
    window.location.href = `mailto:privacy@section.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Data Deletion</h2>
        <p className="text-gray-600">
          Submit a request to delete all your personal data in accordance with CCPA.
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
          <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Important Information
        </h3>
        <p className="text-sm text-gray-700">
          Once approved, this action will permanently delete:
        </p>
        <ul className="list-disc pl-6 text-sm text-gray-700 mt-2 space-y-1">
          <li>All your chat history and questions asked</li>
          <li>Your account information (name, email)</li>
          <li>All session logs and analytics data associated with your account</li>
          <li>Access to this investor data room</li>
        </ul>
        <p className="text-sm text-gray-700 mt-3">
          <strong>This action cannot be undone.</strong> You will need to be re-invited to access the data room in the future.
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-2">Processing Timeline</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• Request review: Within 5 business days</li>
          <li>• Data deletion completion: Within 45 days (CCPA requirement)</li>
          <li>• Confirmation email sent when complete</li>
        </ul>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-2">Your Account</h3>
        <p className="text-sm text-gray-700">
          <strong>Email:</strong> {user?.primaryEmailAddress?.emailAddress}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
        >
          Cancel
        </button>
        <button
          onClick={handleEmailRequest}
          className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
        >
          Send Deletion Request Email
        </button>
      </div>

      <p className="text-xs text-gray-500 mt-6 text-center">
        Clicking the button above will open your email client with a pre-filled deletion request.
        Send it to <strong>privacy@section.com</strong> to submit your request.
      </p>
    </div>
  );
};

export default DataDeletionRequest;
